import { PreferenceChipGroup } from '@/components/companion/PreferenceChipGroup';
import { buildGeoOptions } from '@/components/companion/geo-options';
import { nicheLabel, sizeTargetLabel } from '@/components/companion/preference-labels';
import { Accordion, Badge, Box, HStack, Skeleton, Text, VStack } from '@chakra-ui/react';
import { NICHE_VALUES, SIZE_TARGETS, type BrandDiscoveryPreferences } from '@fluenceur/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type PreferenceBlocksProps = {
  /** Current draft. null = loading state (skeletons shown). */
  draft: BrandDiscoveryPreferences | null;
  /** Called with a partial patch to merge into the draft. */
  onPatch: (changes: Partial<BrandDiscoveryPreferences>) => void;
};

interface SectionHeaderProps {
  id: string;
  label: string;
  subtitle: string;
  count?: number;
  withSeparator?: boolean;
}

/** Group header with optional top separator and selection count. */
const SectionHeader = ({
  id,
  label,
  subtitle,
  count,
  withSeparator = false,
}: SectionHeaderProps) => (
  <Box
    borderTopWidth={withSeparator ? '1px' : undefined}
    borderColor="border.subtle"
    pt={withSeparator ? '6' : undefined}
  >
    <Text id={id} fontSize="sm" fontWeight="medium" color="fg">
      {label}
      {count != null && count > 0 && (
        <Text as="span" fontSize="xs" fontWeight="normal" color="fg.muted" ml={1.5}>
          ({count})
        </Text>
      )}
    </Text>
    <Text fontSize="xs" color="fg.muted" mt={0.5}>
      {subtitle}
    </Text>
  </Box>
);

/**
 * The four-block preference editor (sizes, niches, geo, anti-niches).
 * Shared between the companion wizard step and the Settings Companion IA page.
 * Renders skeletons when draft is null, chips when data is available.
 * Anti-niches live under a collapsible "Refine" disclosure.
 *
 * Consumers own the draft state and the save action; this component is
 * display-only (no mutations, no network calls).
 */
const PreferenceBlocks = ({ draft, onPatch }: PreferenceBlocksProps) => {
  const { t, i18n } = useTranslation();

  const geoOptions = useMemo(
    () => buildGeoOptions(draft?.geo, i18n.language),
    [draft?.geo, i18n.language]
  );

  const sizeOptions = useMemo(
    () => SIZE_TARGETS.map((size) => ({ value: size, label: sizeTargetLabel(size, t) })),
    [t]
  );

  const nicheOptions = useMemo(
    () => NICHE_VALUES.map((niche) => ({ value: niche, label: nicheLabel(niche, t) })),
    [t]
  );

  if (!draft) {
    return (
      <VStack gap={6} align="stretch">
        <Skeleton height="20" borderRadius="lg" />
        <Skeleton height="20" borderRadius="lg" />
        <Skeleton height="20" borderRadius="lg" />
      </VStack>
    );
  }

  return (
    <VStack gap={4} align="stretch">
      {/* Sizes */}
      <VStack gap={3} align="stretch" role="group" aria-labelledby="prefs-sizes-label">
        <SectionHeader
          id="prefs-sizes-label"
          label={t('companion.wizard.preferences.sizes')}
          subtitle={t('companion.wizard.preferences.sizesSubtitle')}
          count={draft.sizeTargets.length}
        />
        <PreferenceChipGroup
          options={sizeOptions}
          selected={draft.sizeTargets}
          onChange={(next) => onPatch({ sizeTargets: next })}
        />
      </VStack>

      {/* Niches */}
      <VStack gap={3} align="stretch" role="group" aria-labelledby="prefs-niches-label">
        <SectionHeader
          id="prefs-niches-label"
          label={t('companion.wizard.preferences.niches')}
          subtitle={t('companion.wizard.preferences.nichesSubtitle')}
          count={draft.nicheTags.length}
          withSeparator
        />
        <PreferenceChipGroup
          options={nicheOptions}
          selected={draft.nicheTags}
          onChange={(next) => onPatch({ nicheTags: next })}
          collapsible
          addLabel={t('companion.wizard.preferences.addNiche')}
        />
      </VStack>

      {/* Geo */}
      <VStack gap={3} align="stretch" role="group" aria-labelledby="prefs-geo-label">
        <SectionHeader
          id="prefs-geo-label"
          label={t('companion.wizard.preferences.geo')}
          subtitle={t('companion.wizard.preferences.geoSubtitle')}
          count={draft.geo.length}
          withSeparator
        />
        <PreferenceChipGroup
          options={geoOptions}
          selected={draft.geo}
          onChange={(next) => onPatch({ geo: next })}
        />
      </VStack>

      {/* Anti-niches: distinct panel so the disclosure reads as its own zone */}
      <Box borderWidth="1px" borderColor="border" borderRadius="lg" bg="bg.panel" px={4} mt={2}>
        <Accordion.Root collapsible>
          <Accordion.Item value="refine" border="none">
            <Accordion.ItemTrigger cursor="pointer" py={3.5}>
              <VStack flex="1" align="flex-start" gap={0.5}>
                <HStack gap={2}>
                  <Text fontSize="sm" fontWeight="medium" color="fg">
                    {t('companion.wizard.preferences.refine')}
                  </Text>
                  {draft.antiNiche.length > 0 && (
                    <Badge size="sm" variant="subtle" colorPalette="lilac">
                      {draft.antiNiche.length}
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="xs" fontWeight="normal" color="fg.muted">
                  {t('companion.wizard.preferences.antiNichesSubtitle')}
                </Text>
              </VStack>
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent>
              <Accordion.ItemBody pt={1} pb={4}>
                <PreferenceChipGroup
                  options={nicheOptions}
                  selected={draft.antiNiche}
                  onChange={(next) => onPatch({ antiNiche: next })}
                  collapsible
                  addLabel={t('companion.wizard.preferences.addNiche')}
                />
              </Accordion.ItemBody>
            </Accordion.ItemContent>
          </Accordion.Item>
        </Accordion.Root>
      </Box>
    </VStack>
  );
};

export default PreferenceBlocks;
